import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { useTimerContext } from "@/lib/timerContext";
import { api, TimeEntry, Account, ActivityCode, PayPeriod, Project } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/custom/StatusPill";
import { format, isSameDay, eachDayOfInterval, isWeekend, parseISO } from "date-fns";
import { CalendarClock, AlertCircle, Loader2, ChevronLeft, ChevronRight, Printer, Edit2, Trash2, Plus, PenLine, ChevronDown } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";

export default function ThisPayPeriod() {
  const { user } = useAuth();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { openSheet } = useTimerContext();
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
  const printRef = useRef<HTMLDivElement>(null);

  // Manual entry form
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualDate, setManualDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [manualAccount, setManualAccount] = useState("");
  const [manualProject, setManualProject] = useState("none");
  const [manualProjects, setManualProjects] = useState<Project[]>([]);
  const [manualCode, setManualCode] = useState("");
  const [manualHours, setManualHours] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const payPeriod = payPeriods[currentPeriodIndex] || null;
  const [todaysPeriodIndex, setTodaysPeriodIndex] = useState(0);

  const loadEntries = useCallback(async (period: PayPeriod) => {
    try {
      const entriesData = await api.getTimeEntries({
        startDate: period.start_date,
        endDate: period.end_date,
      });
      setEntries(entriesData);
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [periodsData, accountsData, codesData] = await Promise.all([
          api.getPayPeriods(),
          api.getAccounts(),
          api.getActivityCodes(),
        ]);
        
        // Sort by start_date descending (newest first)
        const sortedPeriods = periodsData.sort((a, b) => 
          new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
        );
        
        // Find today's period index
        const today = new Date();
        const todayStr = format(today, "yyyy-MM-dd");
        const todayIdx = sortedPeriods.findIndex(p => 
          todayStr >= p.start_date && todayStr <= p.end_date
        );
        
        setPayPeriods(sortedPeriods);
        setAccounts(accountsData);
        setActivityCodes(codesData);
        setTodaysPeriodIndex(todayIdx >= 0 ? todayIdx : 0);
        setCurrentPeriodIndex(todayIdx >= 0 ? todayIdx : 0);

        if (sortedPeriods.length > 0) {
          const initialPeriod = sortedPeriods[todayIdx >= 0 ? todayIdx : 0];
          await loadEntries(initialPeriod);
        }
      } catch (error) {
        console.error("Failed to load pay period data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [loadEntries]);

  useEffect(() => {
    if (payPeriod) {
      loadEntries(payPeriod);
    }
  }, [currentPeriodIndex, payPeriod, loadEntries]);

  const handlePrevPeriod = () => {
    if (currentPeriodIndex < payPeriods.length - 1) {
      setCurrentPeriodIndex(currentPeriodIndex + 1);
    }
  };

  const handleNextPeriod = () => {
    if (currentPeriodIndex > 0) {
      setCurrentPeriodIndex(currentPeriodIndex - 1);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setLocation(`/today?edit=${entry.id}&date=${entry.entry_date}`);
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!(await askConfirm({
      title: "Delete this time entry?",
      subject: "time entry",
      body: "will be deleted.",
      confirmLabel: "Delete",
      destructive: true,
    }))) return;

    try {
      await api.deleteTimeEntry(entryId);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast({ title: "Entry deleted" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleManualAccountChange = (accountId: string) => {
    setManualAccount(accountId);
    setManualProject("none");
    if (accountId && accountId !== "none") {
      api.getProjects({ accountId }).then(setManualProjects).catch(() => setManualProjects([]));
    } else {
      setManualProjects([]);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualCode || !manualHours) {
      toast({ title: "Activity code and hours are required", variant: "destructive" });
      return;
    }
    const hours = parseFloat(manualHours);
    if (isNaN(hours) || hours <= 0) {
      toast({ title: "Enter a valid number of hours", variant: "destructive" });
      return;
    }
    setManualSubmitting(true);
    try {
      const entry = await api.createTimeEntry({
        entry_date: manualDate,
        account_id: manualAccount && manualAccount !== "none" ? manualAccount : null,
        project_id: manualProject && manualProject !== "none" ? manualProject : null,
        activity_code: manualCode,
        minutes: Math.round(hours * 60),
        notes: manualNotes.trim() || undefined,
        billable: activityCodes.find(c => c.code === manualCode)?.billable_default ?? true,
      });
      setEntries(prev => [...prev, entry]);
      setManualHours("");
      setManualNotes("");
      toast({ title: "Time entry logged" });
    } catch (error: any) {
      toast({ title: "Failed to log time", description: error.message, variant: "destructive" });
    } finally {
      setManualSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!payPeriod || !payPeriod.start_date || !payPeriod.end_date) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
          <AlertCircle className="w-12 h-12 text-muted-foreground" />
          <p className="text-muted-foreground">No pay periods found.</p>
        </div>
      </Layout>
    );
  }

  const today = new Date();
  const startDate = parseISO(payPeriod.start_date);
  const endDate = parseISO(payPeriod.end_date);
  const isCurrentPeriod = currentPeriodIndex === todaysPeriodIndex;
  const isPreviousPeriod = currentPeriodIndex > todaysPeriodIndex;
  const isNextPeriod = currentPeriodIndex < todaysPeriodIndex;
  const isPeriodClosed = payPeriod.status === "closed";

  // Get period label
  const getPeriodLabel = () => {
    if (isCurrentPeriod) return "Current Pay Period";
    if (isPreviousPeriod) return "Previous Pay Period";
    if (isNextPeriod) return "Next Pay Period";
    return "Pay Period";
  };

  const totalHours = entries.reduce((sum, e) => sum + e.minutes, 0) / 60;
  const billableHours = entries.reduce((sum, e) => sum + (e.billable ? e.minutes : 0), 0) / 60;
  const ptoHours = entries.reduce((sum, e) => {
    const code = activityCodes.find(c => c.code === e.activity_code);
    return sum + (["pto", "holiday"].includes(code?.category || "") ? e.minutes : 0);
  }, 0) / 60;

  const daysInPeriod = eachDayOfInterval({ start: startDate, end: endDate });

  const sortedEntries = [...entries].sort((a, b) => 
    new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime()
  );

  return (
    <Layout>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-timesheet, .print-timesheet * { visibility: visible; }
          .print-timesheet { 
            position: fixed; 
            left: 0; 
            top: 0; 
            right: 0;
            width: 100%; 
            max-width: 100%;
            margin: 0 auto;
            padding: 0;
            font-size: 11px;
            background: white;
          }
          .no-print { display: none !important; }
          .screen-only { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.75in; size: letter; }
        }
      `}</style>

      <div className="space-y-6 screen-only">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrevPeriod}
              disabled={currentPeriodIndex >= payPeriods.length - 1}
              data-testid="button-prev-period"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div>
              <h1 className="text-2xl font-heading font-bold tracking-tight" data-testid="text-page-title">
                {format(startDate, "MMM d")} – {format(endDate, "MMM d, yyyy")}
              </h1>
              <p className="text-sm text-muted-foreground" data-testid="text-pay-period-dates">
                {getPeriodLabel()}
                {isPeriodClosed && (
                  <span className="ml-2 text-amber-600 font-medium">(Closed)</span>
                )}
                {!isCurrentPeriod && (
                  <Button 
                    variant="link" 
                    className="h-auto p-0 ml-2 text-xs" 
                    onClick={() => setCurrentPeriodIndex(todaysPeriodIndex)}
                  >
                    Back to Current
                  </Button>
                )}
              </p>
            </div>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleNextPeriod}
              disabled={currentPeriodIndex <= 0}
              data-testid="button-next-period"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            {!isPeriodClosed && (
              <Button
                variant="outline"
                onClick={() => openSheet(format(new Date(), "yyyy-MM-dd"))}
                data-testid="button-add-entry"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </Button>
            )}
            <Button
              variant={showManualForm ? "secondary" : "outline"}
              onClick={() => setShowManualForm(v => !v)}
              data-testid="button-manual-entry"
            >
              <PenLine className="w-4 h-4 mr-2" />
              Manual Entry
              <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${showManualForm ? "rotate-180" : ""}`} />
            </Button>
            <Button variant="outline" onClick={handlePrint} data-testid="button-print">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            
            <div className="flex gap-2">
              <Card className="px-4 py-2 bg-primary text-primary-foreground border-none shadow-lg">
                <div className="text-xs opacity-80 uppercase tracking-wider font-semibold">Total</div>
                <div className="text-2xl font-bold font-heading" data-testid="text-total-hours">{totalHours.toFixed(2)}</div>
              </Card>
              <Card className="px-4 py-2">
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Billable</div>
                <div className="text-2xl font-bold font-heading text-foreground" data-testid="text-billable-hours">{billableHours.toFixed(2)}</div>
              </Card>
              <Card className="px-4 py-2">
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">PTO</div>
                <div className="text-2xl font-bold font-heading text-foreground" data-testid="text-pto-hours">{ptoHours.toFixed(2)}</div>
              </Card>
            </div>
          </div>
        </div>

        {/* Manual entry form */}
        {showManualForm && (
          <Card className="border-dashed">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={manualDate}
                    onChange={e => setManualDate(e.target.value)}
                    className="h-8 text-sm"
                    max={payPeriod.end_date}
                    min={payPeriod.start_date}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Client</Label>
                  <Select value={manualAccount || "none"} onValueChange={handleManualAccountChange}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No client</SelectItem>
                      {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Project</Label>
                  <Select value={manualProject} onValueChange={setManualProject} disabled={!manualAccount || manualAccount === "none"}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No project" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {manualProjects.map(p => {
                        const period = p.computed_accounting_period ?? (p.tax_year ? String(p.tax_year) : null);
                        return <SelectItem key={p.id} value={p.id}>{period ? `${p.title} — ${period}` : p.title}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Activity</Label>
                  <Select value={manualCode || "none"} onValueChange={v => setManualCode(v === "none" ? "" : v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select…</SelectItem>
                      {activityCodes.map(c => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hours</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="24"
                    placeholder="e.g. 1.5"
                    value={manualHours}
                    onChange={e => setManualHours(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notes</Label>
                  <div className="flex gap-1">
                    <Input
                      placeholder="Optional notes"
                      value={manualNotes}
                      onChange={e => setManualNotes(e.target.value)}
                      className="h-8 text-sm flex-1"
                      onKeyDown={e => { if (e.key === "Enter") handleManualSubmit(); }}
                    />
                    <Button
                      size="sm"
                      className="h-8 px-3"
                      onClick={handleManualSubmit}
                      disabled={manualSubmitting || !manualCode || !manualHours}
                    >
                      {manualSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Log"}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-t-4 border-t-primary shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              Daily Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {daysInPeriod.map(day => {
                const dayStr = format(day, "yyyy-MM-dd");
                const dayEntries = entries.filter(e => e.entry_date === dayStr);
                const dayTotal = dayEntries.reduce((s, e) => s + e.minutes, 0) / 60;
                const isToday = isSameDay(day, today);
                const isWeekendDay = isWeekend(day);
                const isMissing = !isWeekendDay && dayTotal === 0 && day < today && isCurrentPeriod;

                if (isWeekendDay && dayTotal === 0) return null;

                return (
                  <div key={dayStr} className={`border rounded-lg p-4 ${isToday ? 'bg-primary/5 border-primary/20 ring-1 ring-primary/10' : 'bg-card'}`} data-testid={`card-day-${dayStr}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 text-center rounded-md py-1 border ${isMissing ? 'bg-destructive/10 border-destructive/20 text-destructive' : 'bg-muted border-border text-foreground'}`}>
                          <div className="text-xs font-medium uppercase">{format(day, "MMM")}</div>
                          <div className="text-xl font-bold leading-none">{format(day, "d")}</div>
                        </div>
                        <div>
                          <h4 className={`font-semibold ${isToday ? 'text-primary' : ''}`}>
                            {format(day, "EEEE")} {isToday && "(Today)"}
                          </h4>
                          {isMissing && (
                            <p className="text-xs text-destructive font-medium flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Missing time entry
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold font-mono" data-testid={`text-day-total-${dayStr}`}>{dayTotal.toFixed(2)}h</div>
                      </div>
                    </div>

                    {dayEntries.length > 0 && (
                      <div className="pl-14 space-y-2">
                        {dayEntries.map(entry => {
                          const account = entry.accounts || accounts.find(a => a.id === entry.account_id);
                          const code = entry.activity_codes || activityCodes.find(c => c.code === entry.activity_code);
                          const hours = entry.minutes / 60;
                          const canEdit = entry.status === "draft" && !isPeriodClosed;
                          
                          return (
                            <div key={entry.id} className="text-sm p-2 bg-muted/30 rounded hover:bg-muted/50 transition-colors group" data-testid={`row-entry-${entry.id}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <StatusPill status={entry.status} className="scale-75 origin-left flex-shrink-0" />
                                  <span className="font-medium text-foreground truncate">{account?.account_name || "No Account"}</span>
                                  <span className="text-muted-foreground hidden sm:inline flex-shrink-0">•</span>
                                  <span className="text-muted-foreground truncate hidden sm:inline">{code?.name || entry.activity_code}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <div className="font-mono text-foreground font-medium">
                                    {hours.toFixed(2)}h
                                  </div>
                                  {canEdit && (
                                    <div className="flex gap-1 no-print">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-primary"
                                        onClick={() => handleEditEntry(entry)}
                                        data-testid={`button-edit-${entry.id}`}
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                        onClick={() => handleDeleteEntry(entry.id)}
                                        data-testid={`button-delete-${entry.id}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {entry.notes && (
                                <p className="text-xs text-muted-foreground/80 italic mt-1 ml-6 pl-2 border-l-2 border-muted">
                                  "{entry.notes}"
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="print-timesheet hidden print-only" ref={printRef}>
        <div style={{ borderBottom: '2px solid #1a365d', paddingBottom: '12px', marginBottom: '16px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1a365d', margin: 0 }}>
            Carpenter &amp; Company, CPAs, PC
          </h1>
          <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>Employee Timesheet</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '12px' }}>
          <div>
            <p style={{ margin: '0 0 4px 0' }}><strong>Employee:</strong> {user?.name || user?.email || 'N/A'}</p>
            <p style={{ margin: 0 }}><strong>Pay Period:</strong> {format(startDate, "MMMM d, yyyy")} - {format(endDate, "MMMM d, yyyy")}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: '0 0 4px 0' }}><strong>Pay Date:</strong> {payPeriod.pay_date ? format(parseISO(payPeriod.pay_date), "MMMM d, yyyy") : 'N/A'}</p>
            <p style={{ margin: 0 }}><strong>Status:</strong> {payPeriod.status === 'closed' ? 'Closed' : 'Open'}</p>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px', marginBottom: '16px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'left', fontWeight: '600' }}>Date</th>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'left', fontWeight: '600' }}>Client/Account</th>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'left', fontWeight: '600' }}>Activity</th>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'center', fontWeight: '600' }}>Hours</th>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'center', fontWeight: '600' }}>Billable</th>
              <th style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'left', fontWeight: '600' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry, idx) => {
              const account = entry.accounts || accounts.find(a => a.id === entry.account_id);
              const code = entry.activity_codes || activityCodes.find(c => c.code === entry.activity_code);
              const hours = entry.minutes / 60;
              return (
                <tr key={entry.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px' }}>
                    {format(parseISO(entry.entry_date), "MM/dd/yyyy")}
                  </td>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px' }}>
                    {account?.account_name || 'N/A'}
                  </td>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px' }}>
                    {code?.name || entry.activity_code}
                  </td>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'center', fontFamily: 'monospace' }}>
                    {hours.toFixed(2)}
                  </td>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px', textAlign: 'center' }}>
                    {entry.billable ? 'Yes' : 'No'}
                  </td>
                  <td style={{ border: '1px solid #d1d5db', padding: '6px 8px', maxWidth: '200px' }}>
                    {entry.notes || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #1a365d', paddingTop: '12px', marginTop: '16px' }}>
          <div style={{ fontSize: '11px' }}>
            <p style={{ margin: '0 0 4px 0' }}><strong>Total Hours:</strong> {totalHours.toFixed(2)}</p>
            <p style={{ margin: '0 0 4px 0' }}><strong>Billable Hours:</strong> {billableHours.toFixed(2)}</p>
            <p style={{ margin: 0 }}><strong>PTO/Leave Hours:</strong> {ptoHours.toFixed(2)}</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '10px', color: '#666' }}>
            <p style={{ margin: '0 0 4px 0' }}>Generated: {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}</p>
            <p style={{ margin: 0 }}>Total Entries: {entries.length}</p>
          </div>
        </div>

        <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
          <div style={{ width: '45%' }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: '4px' }}>
              Employee Signature
            </div>
          </div>
          <div style={{ width: '45%' }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: '4px' }}>
              Manager Approval
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
